import React, { useState, useEffect, useMemo, useRef } from 'react';
import Grid from '@mui/material/Grid';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { Listing } from 'src/interfaces/idashboard';
import { IndividualListingModalPropTypes } from 'src/interfaces/iproptypes';
import PerformanceData from './PerformanceData';
import TopBar from './TopBar';
import styles from './style';
import {
  checkListingRecentlyAdded,
  checkListingInactive,
  checkListingUnlisted,
  checkAdrAnomaly,
  processIndividualListingData,
} from 'src/utils/functions';
import { useSelector } from 'react-redux';
import HistoricalPerformanceGraph from './HistoricalPerformanceGraph';
import { COLOR_RED, DEMO_INDIVIDUAL_HOST_DATA } from 'src/utils/const';
import { Alert, CircularProgress, Container, Tooltip, Typography } from '@mui/material';
import TabBar from 'src/App/Dashboards/Market/Components/Tabs/TabBar';
import { COLOR_PRIMARY_2, WATER_MARK_COLOR } from 'src/utils/colors';
import FavoriteBorderRoundedIcon from '@mui/icons-material/FavoriteBorderRounded';
import DummyListingImage1 from 'src/App/assets/listing_dummy_1.jpg';
import DummyListingImage2 from 'src/App/assets/listing_dummy_2.jpg';
import DummyListingImage3 from 'src/App/assets/listing_dummy_3.jpg';
import BookingTypeDemoGraph from 'src/App/assets/booking_type_demo_graph.jpg';
import ListingDetailsSection from './ListingDetailsSection';
import { User } from 'src/interfaces/iuser';
import { callApi } from 'src/core/callApi';
import HostModal from 'src/App/Dashboards/Market/Pages/OverviewPage/UtilityComponents/HostModal';
import PerformanceExplanationSection from './PerformanceExplanationSection';
import RateLimitAlert from '../../Components/Alerts/RateLimitAlert';

const SubSectionTabs = ({ isMobile, tabList, currentTabView, isMobileScreen }) => {
  return (
    <>
      <TabBar
        tabList={tabList}
        selectedCurrentTab={currentTabView}
        color={COLOR_PRIMARY_2}
        renderLabelCondition={() => true}
        actionButton={
          <Tooltip title={'Save Listing (Coming Soon)!'}>
            <FavoriteBorderRoundedIcon
              sx={{
                color: COLOR_RED,
                padding: '15px',
                borderRadius: '800px',
                cursor: 'pointer',
                transition: 'all 0.25s ease',
                '&:hover': {
                  backgound: '#ccc',
                },
              }}
            />
          </Tooltip>
        }
        showActionButton={isMobileScreen ? false : true}
        isMobile={isMobile}
        scrollButtons={isMobile}
      />
    </>
  );
};

const ComingSoonOverlay = ({ additionalStyling }: { additionalStyling?: any }) => {
  return (
    <Box>
      <Typography
        variant="h4"
        sx={{
          color: WATER_MARK_COLOR,
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          fontWeight: 'bold',
          textShadow: '0 1px 0 rgba(255, 255, 255, 0.4)',
          ...(additionalStyling && additionalStyling),
        }}
      >
        Coming Soon
      </Typography>
    </Box>
  );
};

const IndividualListingModalV2 = ({
  open,
  onClose,
  data,
  currency,
  isDisabled,
  listings,
  subscriptionModalOpen,
  setSubscriptionModalOpen,
  handleOnSampleClick,
  estimatorSampleListingData,
  isEstimator,
  openDemoBookingForm,
  setDemoFormHeadingFor,
  isHistoricalDataHidden,
  onEstimatorLockedMarkerHistoricalDataClick,
  noListingData,
  referenceADR,
  cachedMarketData,
  isApiRateLimited,
  marketID,
  doNotRefetchListingData
}: IndividualListingModalPropTypes) => {
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const currentMarketUrl = useRef(window.location.pathname + window.location.search);

  const [currentTabView, setCurrentTabView] = useState<
    'listing' | 'overview' | 'seasonality' | 'hostInsights' | 'bookingDetails' | 'similarListings'
  >('listing');

  const [currentListingData, setCurrentListingData] = useState<any[] | Listing>(data);

  const [showLoadingScreen, setShowLoadingScreen] = useState<boolean>(false);

  const [hostData, setHostData] = useState<any | null | 'loading' | 'failed'>(null);

  const user: User = useSelector((state: any) => state.dashboard.user);

  const getHostData = async (hostID: string) => {
    setHostData('loading');
    const url = 'https://xfs76ukxc5.execute-api.us-east-2.amazonaws.com/default/getTopHostDetails';
    const config = {
      queryParams: {
        hostID: hostID,
        returnUserAccess: true,
        apiType: 'v3'
      },
    };
    const response = await callApi(url, config, true);
    const hostData = response?.message;
    if (hostData) {
      setHostData(hostData);
    } else {
      setHostData('failed');
    }
  };

  const refetchListingData = async (listingId: string) => {
    const URL = 'https://8q15spida4.execute-api.us-east-2.amazonaws.com/default/getListingDetails';
    const config = {
      queryParams: {
        listingID: listingId,
        apiType: 'v5',
        marketID: marketID ? marketID : ''
      },
    };
    const resp = await callApi(URL, config, true, false, '', 'GET', true, undefined, true);
    const listingData = resp?.message;

    if (resp.status === 429) {
      return null;
    }

    if (listingData && resp.status !== 429) {
      const transformedData = processIndividualListingData(listingData);
      return transformedData;
    } else {
      return null;
    }
  };

  useEffect(() => {
    if (
      !Object.keys(data).includes('rest_thumbnails') ||
      !data['rest_thumbnails'] ||
      data['rest_thumbnails'].length === 0
    ) {
      const listingId = data['listingID'];
      !doNotRefetchListingData && refetchListingData(listingId).then(resp => {
        if (resp) {
          setCurrentListingData(resp);
        }
      });
    }
  }, []);

  useEffect(() => {
    setCurrentListingData(data);
  }, [data]);

  useEffect(() => {
    if (subscriptionModalOpen) {
      onClose();
    }
  }, [subscriptionModalOpen]);

  const setListingUrl = (listingId: string) => {
    const newPathName = `/listing/${listingId}`;
    window.history.replaceState(null, '', newPathName);
  };

  const setMarketUrlBack = () => {
    currentMarketUrl.current && window.history.replaceState(null, '', currentMarketUrl.current);
  };

  useEffect(() => {
    if (noListingData || isApiRateLimited) return;

    setListingUrl(data['listingID']);
    return () => {
      setMarketUrlBack();
    };
  }, []);

  const isListingRecentlyAdded = checkListingRecentlyAdded(currentListingData as Listing);
  const isListingInactive = checkListingInactive(currentListingData as Listing);
  const isListingUnlisted = checkListingUnlisted(currentListingData as Listing);
  const isAdrAnomaly = checkAdrAnomaly(
    (currentListingData as Listing).adrLTM,
    referenceADR || null
  );

  const PerformanceExplanationComponent = useMemo(
    () => (
      <PerformanceExplanationSection
        listingID={currentListingData['listingID']}
        handleOnSampleClick={handleOnSampleClick}
        currency={currency}
        setMarketUrlBack={setMarketUrlBack}
        triggerModalClose={onClose}
        isDemo={false}
        isDisabled={isDisabled}
        marketID={marketID}
      />
    ),
    [currentListingData['listingID'], currency, isDisabled]
  );

  const ListingTabSection = (
    <ListingDetailsSection
      data={currentListingData}
      isMobileScreen={isMobileScreen}
      cachedMarketData={cachedMarketData}
      PerformanceExplanationComponent={PerformanceExplanationComponent}
    />
  );

  const PerformanceTabSection = (
    <Grid
      container
      direction="row"
      justifyContent="center"
      sx={{ width: isMobileScreen ? '100%' : '100%' }}
      spacing={isMobileScreen ? 0 : 4}
      xs={12}
    >
      <PerformanceData
        data={currentListingData}
        currency={currency}
        handleOnSampleClick={handleOnSampleClick}
        isDisabled={isDisabled}
        isEstimator={isEstimator}
        isListingRecentlyAdded={isListingRecentlyAdded}
        isMobileScreen={isMobileScreen}
        isAdrAnomaly={isAdrAnomaly}
        setMarketUrlBack={setMarketUrlBack}
        onClose={onClose}
      />
    </Grid>
  );

  const SeasonalityTabSection = (
    <Grid
      container
      direction="row"
      justifyContent="flex-start"
      sx={{ width: isMobileScreen ? '100%' : '100%', marginTop: '10px', padding: '0 0 20px 20px' }}
      spacing={isMobileScreen ? 0 : 4}
      xs={12}
    >
      <HistoricalPerformanceGraph
        listingID={currentListingData['listingID']}
        handleOnSampleClick={handleOnSampleClick}
        currency={currency}
        setMarketUrlBack={setMarketUrlBack}
        triggerModalClose={onClose}
        isAdrAnomaly={isAdrAnomaly}
        showOccupancyGraphOnly={false}
      />
    </Grid>
  );

  const HostInsightsTabSection = (
    <Grid
      container
      direction="row"
      justifyContent="center"
      sx={{
        position: 'relative',
        gap: '20px',
        width: '100%',
        marginTop: '10px',
        padding: '0 20px 0 20px',
      }}
      xs={12}
    >
      {hostData === null && (
        <Grid container sx={{ position: 'relative' }}>
          <Box sx={{ width: '100%', filter: 'blur(4px)' }}>
            <HostModal
              open={true}
              onClose={() => { }}
              host={DEMO_INDIVIDUAL_HOST_DATA}
              currency={currency}
              isMobileScreen={isMobileScreen}
              setHost={() => { }}
              handleOnSampleClick={handleOnSampleClick}
              user={user}
              getTableComponent={() => <></>}
              returnModalBodyOnly={true}
              handleUnlockTopHosts={() => { }}
            />
          </Box>
          <Typography
            style={styles.revealButton}
            onClick={() => {
              if (['expert', 'scale'].includes(user.subscriptionPlan)) {
                getHostData(String(currentListingData['hostId']));
              } else {
                handleOnSampleClick();
              }
            }}
            sx={{
              position: 'absolute',
              top: '40px',
              left: '50%',
              transform: 'translate(-50%, 0)',
              textDecoration: 'underline',
            }}
          >
            {!['expert', 'scale'].includes(user.subscriptionPlan)
              ? 'Upgrade to Expert Plan'
              : 'Reveal'}
          </Typography>
        </Grid>
      )}
      {hostData === 'loading' && <CircularProgress />}
      {hostData === 'failed' && (
        <Typography variant="body1">
          <Alert severity="error">
            Only available for hosts with 5+ listings. This host doesn’t meet that requirement.
          </Alert>
        </Typography>
      )}
      {hostData && hostData.host && (
        <Box sx={{ width: '100%' }}>
          <HostModal
            open={true}
            onClose={() => { }}
            host={hostData.host}
            currency={currency}
            isMobileScreen={isMobileScreen}
            setHost={() => { }}
            handleOnSampleClick={handleOnSampleClick}
            user={user}
            getTableComponent={() => <></>}
            returnModalBodyOnly={true}
            handleUnlockTopHosts={() => { }}
          />
        </Box>
      )}
    </Grid>
  );

  const SimilarListingTabSection = (
    <Grid
      container
      direction="row"
      justifyContent="center"
      sx={{
        position: 'relative',
        gap: '20px',
        width: '100%',
        marginTop: '10px',
        padding: '0 20px 0 20px',
      }}
      xs={12}
    >
      <img src={DummyListingImage1} style={{ maxWidth: '220px', width: '100%' }} />
      <img src={DummyListingImage2} style={{ maxWidth: '220px', width: '100%' }} />
      <img src={DummyListingImage3} style={{ maxWidth: '220px', width: '100%' }} />
      <ComingSoonOverlay />
    </Grid>
  );

  const BookingDetailsTabSection = (
    <Grid
      container
      direction="row"
      justifyContent="flex-start"
      sx={{
        position: 'relative',
        gap: '20px',
        width: '100%',
        marginTop: '10px',
        padding: '0 20px 0 20px',
      }}
      xs={12}
    >
      <Grid item xs={12}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Booking Length Analysis
        </Typography>
      </Grid>
      <Grid item sx={{ position: 'relative', maxWidth: '500px', width: '100%' }}>
        <img src={BookingTypeDemoGraph} style={{ maxWidth: '220px', width: '100%' }} />
        <ComingSoonOverlay
          additionalStyling={{
            left: 0,
            transform: 'translate(0, -50%)',
          }}
        />
      </Grid>
    </Grid>
  );

  const tabList = useMemo(
    () => [
      {
        label: 'Overview',
        mobileLabel: 'Overview',
        value: 'overview',
        component: PerformanceTabSection,
        onclick: () => {
          setCurrentTabView('overview');
        },
      },
      {
        label: 'Listing',
        mobileLabel: 'Listing',
        value: 'listing',
        component: ListingTabSection,
        onclick: () => {
          setCurrentTabView('listing');
        },
      },
      {
        label: 'Seasonality',
        mobileLabel: 'Seasonality',
        value: 'seasonality',
        component: SeasonalityTabSection,
        onclick: () => {
          setCurrentTabView('seasonality');
        },
      },
      {
        label: 'Host Insights',
        mobileLabel: 'Host Insights',
        value: 'hostInsights',
        component: HostInsightsTabSection,
        onclick: () => {
          setCurrentTabView('hostInsights');
        },
      },
      {
        label: 'Booking Details',
        mobileLabel: 'Booking Details',
        value: 'bookingDetails',
        component: BookingDetailsTabSection,
        onclick: () => {
          setCurrentTabView('bookingDetails');
        },
      },
      {
        label: 'Similar Listings',
        mobileLabel: 'Similar Listings',
        value: 'similarListings',
        component: SimilarListingTabSection,
        onclick: () => {
          setCurrentTabView('similarListings');
        },
      },
    ],
    [ListingTabSection, PerformanceTabSection]
  );

  return (
    <>
      {/* Main Modal */}
      <Modal open={open} onClose={onClose}>
        <Container
          sx={{
            ...styles.background,
            width: isMobileScreen ? '100vw' : '60vw',
            display: 'flex',
            justifyContent: 'center',
            outline: 'none !important',
            pt: '5px',
            pb: '5px',
            ...(isMobileScreen ? { paddingLeft: '5px', paddingRight: '20px' } : {}),
          }}
          className={isMobileScreen ? 'safe-mobile-height' : ''}
        >
          {showLoadingScreen && (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
              }}
            >
              <CircularProgress size={60} />
            </Box>
          )}

          {isApiRateLimited && !showLoadingScreen && (
            <Grid container justifyContent={'space-between'} alignContent={'flex-start'}>
              <TopBar
                onClose={onClose}
                isMobileScreen={isMobileScreen}
                data={[]}
                title={""}
                noListingData={false}
                userSubscriptionPlan={user.subscriptionPlan}
                isListingRecentlyAdded={false}
                isListingInactive={false}
                isListingUnlisted={false}
                noSampleListingOption={true}
              />
              <RateLimitAlert handleOnSampleClick={handleOnSampleClick} />
            </Grid>
          )}

          {/* Outer Grid Container */}
          {!showLoadingScreen && !isApiRateLimited && (
            <Grid container justifyContent={'space-between'} alignContent={'flex-start'}>
              <TopBar
                onClose={onClose}
                isMobileScreen={isMobileScreen}
                data={currentListingData}
                title={currentListingData['name']}
                noListingData={noListingData}
                userSubscriptionPlan={user.subscriptionPlan}
                isListingRecentlyAdded={isListingRecentlyAdded}
                isListingInactive={isListingInactive}
                isListingUnlisted={isListingUnlisted}
              />

              {
                noListingData ? (
                  <Typography variant="body1" sx={{ marginTop: '50px' }}>
                    Listing is not available!
                  </Typography>
                )
                  : (
                    <>
                      <SubSectionTabs
                        isMobile={isMobileScreen}
                        tabList={tabList}
                        currentTabView={currentTabView}
                        isMobileScreen={isMobileScreen}
                      />

                      {tabList.map(tab => (
                        <div
                          key={tab.value}
                          style={{
                            display: currentTabView === tab.value ? 'flex' : 'none',
                            justifyContent: 'center',
                            width: '100%',
                            padding: isMobileScreen ? '0px' : '20px',
                          }}
                        >
                          {tab.component}
                        </div>
                      ))}
                    </>
                  )}
            </Grid>
          )}
        </Container>
      </Modal>
    </>
  );
};

export default IndividualListingModalV2;
